/* eslint-disable */
import React, { useState } from "react";
import PropTypes from "prop-types";
import TdFlowConnected from "./td-flow-connected";
import InsuranceSettings from "./insurance-settings/insurance-settings";
import PricePrescriptions from "./price-prescriptions/price-prescriptions";
import FindProviders from "./find-providers/find-providers";
import FindFacilities from "./find-facilities/find-facilities";
import loginService from "../utils/login-service";
import validators from "../utils/validators";
import env, { setEnv } from "../utils/env";
import { constants } from "../resource/constants";
import PriceProceduresWrapper from "./price-procedure/price-procedures-wrapper";
import FindRecipes from "./find-recipes/find-recipes";
import { attachSlash, validateUrl } from "../utils/url-helper";
import { paths } from "../resource/paths";

/**
 * A wrapper component for td-flow that passes in props and configurations to \<TdFlowConnected>.
 * Handles authorization and log in, and sets logged in member ID to state.
 * @function TdFlowWrapper
 * @param {object} props Configurations passed down from client application
 * @param {string} props.path The name of a screen endpoint
 * @param {object} props.authorization Login credentials (ex. username, password, ssoToken)
 * @param {object} props.appConfiguration Contains configurations for app setup
 * @param {string} [props.appConfiguration.initialState] Configuration object for the requested flow from the client, setting some necessary state for the requested flow
 * @param {string} [props.appConfiguration.googleApiKey] Google API key for Google Maps pharmacy search
 * @param {string} props.appConfiguration.baseUrl Base URL of Mobile API endpoint; Must include trailing "/" at end of URL string
 * @param {string} props.appConfiguration.apiToken  API token for Mobile API endpoint TODO: Get context from MAPI Dev
 * @param {string} props.appConfiguration.bundleIdentifier App version (ex. 'com.td-core')
 * @param {string} [props.appConfiguration.fieldConfigurations={validators}] Configuration object for field validators
 * @param {string} [props.appConfiguration.authToken] Authorization token for the current session
 * @param {string} [props.appConfiguration.authorizedMemberId=null] Passed from client as the current logged in member id, if not present will need to re-authenticate.
 * @param {string} [props.appConfiguration.locale="en"] Configures the language of the app, defaults to English
 * @param {bool} [props.appConfiguration.showBackButton=true] Configures whether or not the back button is shown
 * @param {bool} [props.appConfiguration.browserNavigationEnabled=true] If true, browser back button navigates to previous screen in WebSDK flow; if false browser back button will navigate to page before entering WebSDK
 * @param {string} [props.appConfiguration.piwikAuthToken="84c3a32e4b82f12ff5e751ecea8e13ad"] Piwik authentication token
 * @param {string} [props.appConfiguration.paypalClientId] Paypal credentials for user payment
 * @param {string} [props.appConfiguration.paypalMerchantId] Paypal credentials for user payment
 * @param {string} [props.appConfiguration.idSite="87"] Piwik Analytics identifier for monitoring
 *
 * @type {function}
 * @returns [TdFlowConnected]{@link TdFlowConnected}
 */
const TdFlowWrapper = props => {
  const {
    authorization,
    path,
    customFlowEndpoint,
    pathOption = "",
    appConfiguration: {
      initialState = {},
      googleApiKey,
      baseUrl,
      baseHyUrl,
      baseHyNoApiUrl,
      apiToken,
      bundleIdentifier,
      fieldConfigurations = { validators },
      hyJwtToken = "",
      authToken = "",
      authorizedMemberId = null,
      locale = "en",
      showBackButton = true,
      browserNavigationEnabled = true,
      piwikAuthToken = "84c3a32e4b82f12ff5e751ecea8e13ad",
      paypalClientId,
      paypalMerchantId,
      idSite = "87", // "member-web dev"
      oauthRefreshToken = "",
      loggingEnabled = false,
      getContainerSize,
      additionalData,
      authMethod = "Bearer",
      inboxConfig
    },
    isPageReloaded = false
  } = props;

  /**
   * TODO: Move this initialConfigs to a separate Constants file?
   */
  const initialConfigs = {
    [paths.CONSULT_ASSESSMENT]: { url: "/screens/intakes/primary_care/new" },
    [paths.RAV]: { url: "/screens/request_consult/start" },
    [paths.EDIT_BP_CUFF_SHIPMENT_ADDRESS]: {
      url: "/screens/request_consult/vpc/bp_cuff_shipment/edit_address"
    },
    [paths.OPT_OUT_BP_CUFF_SHIPMENT]: {
      url: "/screens/request_consult/vpc/bp_cuff_shipment/clarify_opt_out"
    },
    [paths.CARE_TEAM]: { url: "/screens/medical_info/care_team" },
    [paths.CARE_PLAN]: { url: "/screens/medical_info/care_plans" },
    [paths.CARE_TEAM_SELECT]: { url: "/screens/primary_care_providers/list" },
    [paths.MESSAGE_CENTER]: { url: "/screens/message_center" },
    [paths.ACCOUNT_SETTINGS]: { url: "/screens/account_settings" },
    [paths.MEDICAL_HISTORY]: { url: "/screens/medical_info" },
    [paths.VITALS]: { url: "/screens/medical_info/vitals" },
    [paths.PRICING_TABLE]: { url: "/screens/my_account/pricing_table" },
    [paths.EXPERT_MEDICAL_OPINION]: {
      url: "/screens/expert_medical_opinion/choose_applicant"
    },
    [paths.CHECKLIST]: { url: "screens/checklist" },
    [paths.MENTAL_HEALTH_NAVIGATOR]: {
      url: "/screens/mental_health_navigator/choose_applicant"
    },
    [paths.PERSONAL_HEALTH_NAVIGATOR]: {
      url: "/screens/personal_health_navigator/choose_applicant"
    },
    [paths.FIND_BEST_DOCTOR]: { url: "/screens/sdk/find_best_doctor" },
    [paths.BACK_CARE]: { url: "/screens/sdk/back_care" },
    [paths.DASHBOARD]: { url: "/screens/dashboard" },
    [paths.DFD_DASHBOARD]: { url: "/screens/homepage" },
    [paths.REGISTRATION]: {
      url: "/screens/background_download",
      initialScreen: "ActivationCallType"
    },
    [paths.INSURANCE_SETTINGS]: { url: "/extension/insuranceSettings" },
    [paths.RESCHEDULE_GM_VISIT]: { url: "/screens/request_consult/edit" },
    [paths.RESCHEDULE_MH_VISIT]: { url: "screens/bh_edit_screens?showHUD=1" },
    [paths.VISIT_CANCELLATION]: {
      url: "/screens/request_consult/pre_cancel_v2"
    },
    [paths.INVOICES]: { url: "screens/invoices" },
    [paths.PRICE_PRESCRIPTIONS]: { url: "/extension/pricePrescriptions" },
    [paths.PRICE_PROCEDURES]: { url: "/extension/priceProcedures" },
    [paths.FIND_PROVIDERS]: { url: "./extension/findProviders" },
    [paths.FIND_FACILITIES]: { url: "/extension/findFacilities" },
    [paths.FIND_RECIPES]: { url: "/extension/findRecipes" },
    [paths.ELITE_DIAGNOSTIC_IMAGING_SERVICE]: {
      url: "/screens/elite_diagnostic_imaging_service/choose_applicant"
    },
    [paths.SELECT_ACCOUNT]: { url: "/screens/select_account" },
    [paths.SECURE_YOUR_ACCOUNT]: { url: "/screens/secure_your_account" },
    [paths.RECOVER_USERNAME]: { url: "/screens/recover_username" },
    [paths.FORGOT_PASSWORD]: { url: "/screens/forgot_password" },
    [paths.RECOVER_PASSWORD]: { url: "/screens/recover_password" },
    [paths.TWO_STEP]: { url: "/screens/authentication/validate_two_step" },
    [paths.SDK_APPOINTMENT_LIST]: { url: "/screens/sdk/appointment_list" },
    [paths.SDK_APPOINTMENT_LIST_REFRESH]: {
      url: "/screens/advancemedical/appointment_list_refresh"
    },
    [paths.MYSTRENGTH_ACTIVITY]: {
      url: "/screens/my_strength/activity"
    },
    [paths.MYSTRENGTH_MODERNIZED_COMPONENTS]: {
      url: "/screens/my_strength/modernized_components"
    },
    [paths.MYSTRENGTH_HISTORY]: { url: "/screens/my_strength/history" },
    [paths.MYSTRENGTH_GET_HELP]: { url: "/screens/my_strength/get_help" },
    [paths.MYSTRENGTH_CONTENT_PREFERENCES]: {
      url: "/screens/my_strength/content_preferences"
    },
    [paths.MYSTRENGTH_CRISIS]: { url: "/screens/my_strength/crisis" },
    [paths.SDK_SERVICES]: { url: "/screens/advancemedical/services" },
    [paths.SDK_START_VIDEO]: { url: "/screens/advancemedical/start_video" },
    [paths.SDK_DASHBOARD]: { url: "/screens/advancemedical/home" },
    [paths.SDK_CONSENT_AGREEMENTS]: {
      url: "/screens/advancemedical/consent_agreements"
    },
    [paths.WELCOME_PAGE]: { url: "/screens/welcomePage" },
    [paths.UNI_SIGNIN]: { url: "/screens/signin" },
    [paths.UNI_REGISTRATION]: {
      url: "/screens/registrations/unified_registration"
    },
    [paths.BASIC_PROFILE]: { url: "/screens/unified_profile/profile" },
    [paths.SECURITY_AND_SIGN_IN]: {
      url: "/screens/unified_profile/security_preferences"
    },
    [paths.EMERGENCY_CONTACT]: {
      url: "/screens/unified_profile/emergency_contacts"
    },
    [paths.COVERAGE_AND_BILLING]: {
      url: "/screens/unified_profile/coverage_and_billing"
    },
    [paths.LANGUAGE_MENU]: { url: "/screens/unified_profile/language_menu" },
    [paths.MY_FAMILY]: { url: "/screens/unified_profile/family" },
    [paths.HELP]: { url: "/screens/unified_profile/get_help" },
    [paths.COMMUNICATION_PREFERENCES]: {
      url: "/screens/unified_profile/communication_preferences"
    },
    [paths.INBOX]: { url: "/screens/inbox" },
    [paths.UNIFIED_PROFILE]: { url: "/screens/unified_profile" },
    [paths.MYSTRENGTH_INTEGRATION]: { url: "/screens/my_strength/home" },
    [paths.MYSTRENGTH_SLEEP_TRACKER]: {
      url: "/screens/my_strength/sleep_tracker"
    },
    [paths.MYSTRENGTH_DIARY_ENTRIES]: {
      url: "/screens/my_strength/sleep_tracker/diary_entries"
    },
    [paths.MYSTRENGTH_GUIDES]: {
      url: "/screens/my_strength/guides/messages_screen"
    },
    [paths.MYSTRENGTH_CREATE_SLEEP_SCHEDULE]: {
      url: "/screens/my_strength/sleep_tracker/sleep_schedule/start"
    },
    [paths.TOBACCO_CESSATION]: {
      url: "/screens/request_consult/start",
      formState: {
        dfdDashboard: true,
        health_category: "General Medical",
        reason_for_visit: "Tobacco Cessation"
      }
    },
    [paths.SEXUAL_HEALTH]: { url: "/screens/sexual_health" },
    [paths.CARE_RECIPIENTS]: { url: "screens/care_recipients" },
    [paths.COVID_ASSESSMENT]: { url: "screens/covid19_assessment" },
    [paths.DEPENDANT_LIST]: { url: "screens/dependent_list" },
    [paths.AUTHORIZED_CONSENTERS]: { url: "screens/authorized_consenters" },
    [paths.OTHER_PROVIDERS]: { url: "screens/doctors" },
    [paths.PHARMACIES]: { url: "screens/pharmacies" },
    [paths.IMAGES_AND_DOCUMENTS]: { url: "screens/images" },
    [paths.VISITS]: { url: "screens/consultations" },
    [paths.CASES]: { url: "screens/emo_cases" },
    [paths.HEALTH]: {
      url: "screens/ehr/get",
      formState: { flow: "medical_history" }
    },
    [paths.MYSTRENGTH_SLEEP_TRACKER_PROGRESS]: {
      url: "screens/my_strength/sleep_tracker/progress_screen"
    },
    [paths.DYNAMIC_TEMPLATES]: { url: "/screens/dynamic_template_test" },
    [paths.JOIN_WAITING_ROOM]: { url: "/screens/video_room" },
    [paths.THANK_YOU]: { url: "/screens/thankYou" },
    [paths.VERIFY_IDENTITY]: { url: "/screens/verify_identity" },
    [paths.MINOR_INVITATION_CONSENTS]: {
      url: "/screens/invitation_consents"
    },
    [paths.MYSTRENGTH_WEEKLY_CHECKIN]: {
      url: "/screens/my_strength/sleep_tracker/weekly_check_in"
    },
    [paths.MYSTRENGTH_EDIT_SLEEP_SCHEDULE]: {
      url: "/screens/my_strength/sleep_tracker/sleep_schedule/edit"
    },
    [paths.MYSTRENGTH_INTERSTITIAL_PAGE]: {
      url: "/screens/my_strength/interstitial_page"
    },
    [paths.MYSTRENGTH_INTERSTITIAL_PAGE_REDIRECT_TO_MH_PLATFORM]: {
      url: "/screens/my_strength/interstitial_page/redirect_to_mh_platform"
    },
    [paths.CARE_OPTION]: {
      url: `/screens/care_option${pathOption !== "" ? `/${pathOption}` : ""}`
    },
    [paths.CANCEL]: { url: "screens/request_consult/pre_cancel" },
    [paths.ALL_CARE_OPTIONS]: { url: "/screens/all_care_products" },
    [paths.MEET_LIVONGO]: { url: "/screens/meet_livongo" },
    [paths.CCM_SSO]: {
      url: "/screens/referral_link",
      formState: {
        dfdDashboard: true,
        health_category: "General Medical",
        service_specialty_code: "DIABETES",
        actor_type: "member",
        cqm_trigger_location: "NavPrograms"
      }
    }
  };

  const [memberId, setMemberId] = useState(Number(authorizedMemberId));
  const [loading, setLoadingStatus] = useState(false);
  constants.googleApiKey = googleApiKey;

  validateUrl(baseUrl, "baseUrl");

  if (baseHyUrl) {
    validateUrl(baseHyUrl, "baseHyUrl");
  }

  if (baseHyNoApiUrl) {
    validateUrl(baseHyNoApiUrl, "baseHyNoApiUrl");
  }

  setEnv({
    BASE_URL: attachSlash(baseUrl),
    BASE_HY_URL: attachSlash(baseHyUrl),
    BASE_HY_NO_API_URL: attachSlash(baseHyNoApiUrl),
    API_TOKEN: apiToken,
    BUNDLE_ID: bundleIdentifier,
    HY_JWT_TOKEN: hyJwtToken,
    LOCALE: locale,
    SHOW_BACK_BUTTON: showBackButton,
    BROWSER_NAVIGATION_ENABLED: browserNavigationEnabled,
    GOOGLE_API_KEY: googleApiKey,
    PIWIK_AUTH_TOKEN: piwikAuthToken,
    PAYPAL_CLIENT_ID: paypalClientId ?? "",
    PAYPAL_MERCHANT_ID: paypalMerchantId ?? "",
    ID_SITE: idSite,
    OAUTH_REFRESH_TOKEN: oauthRefreshToken ?? "",
    LOGGING_ENABLED: loggingEnabled ?? false,
    AUTH_METHOD: env("AUTH_METHOD") || authMethod
  });

  if (inboxConfig) {
    setEnv({
      INBOX_CONFIG: inboxConfig
    });
  }
  if (authToken) {
    setEnv({
      AUTH_TOKEN: authToken,
      MEMBER_ID: memberId
    });
  }

  switch (initialConfigs[path]) {
    case initialConfigs.findProviders:
      return (
        <div className='root-web-sdk'>
          <FindProviders {...props} />
        </div>
      );

    case initialConfigs.findFacilities:
      return (
        <div className='root-web-sdk'>
          <FindFacilities {...props} />
        </div>
      );

    case initialConfigs.findRecipes:
      return (
        <div className='root-web-sdk'>
          <FindRecipes {...props} />
        </div>
      );

    case initialConfigs.pricePrescriptions:
      if (!memberId && !loading && authorization) {
        setLoadingStatus(true);
        loginService(authorization).then(loggedInMemberId => {
          if (loggedInMemberId) {
            setMemberId(loggedInMemberId);
            setLoadingStatus(false);
          }
        });
      }

      return (
        !!(memberId || !authorization) && (
          <div className='root-web-sdk'>
            <PricePrescriptions {...props} />
          </div>
        )
      );

    case initialConfigs.priceProcedures:
      return (
        <div className='root-web-sdk'>
          <PriceProceduresWrapper {...props} />
        </div>
      );
    case initialConfigs.insuranceSettings:
      return (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <div className='root-web-sdk'>
          <InsuranceSettings {...props} />
        </div>
      );

    default:
      if (!memberId && !loading && authorization) {
        setLoadingStatus(true);
        loginService(authorization).then(loggedInMemberId => {
          if (loggedInMemberId) {
            setMemberId(loggedInMemberId);
            setLoadingStatus(false);
          }
        });
      }

      const flowData = initialConfigs[path] || { url: customFlowEndpoint };

      return (
        (memberId || !authorization) && (
          <TdFlowConnected
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
            isPageReloaded={isPageReloaded}
            fieldConfigurations={fieldConfigurations}
            getContainerSize={getContainerSize}
            initialRequest={{
              ...flowData,
              formState: {
                ...flowData.formState,
                ...initialState,
                logged_in_member_id: memberId,
                ...(path === "ccmSso" ? { actor_id: memberId } : {}),
                ...(path === "rescheduleMHVisit" && initialState.consult_id
                  ? { consultation_id: `${initialState.consult_id}` }
                  : {}),
                ...((path === "selectAccount" || path === "recoverPassword") &&
                additionalData
                  ? { demographics: additionalData }
                  : {})
              },
              screenParams: {
                fromDashboard: false
              }
            }}
          />
        )
      );
  }
};

TdFlowWrapper.propTypes = {
  authorization: PropTypes.shape({
    username: PropTypes.string,
    password: PropTypes.string,
    ssoToken: PropTypes.string,
    authToken: PropTypes.string,
    authorizedMemberId: PropTypes.number
  }).isRequired,
  path: PropTypes.string.isRequired,
  customFlowEndpoint: PropTypes.string,
  pathOption: PropTypes.string,
  appConfiguration: PropTypes.shape({
    initialState: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.object])),
    baseHyNoApiUrl: PropTypes.string.isRequired,
    showBackButton: PropTypes.bool,
    browserNavigationEnabled: PropTypes.bool,
    paypalClientId: PropTypes.string,
    paypalMerchantId: PropTypes.string,
    idSite: PropTypes.string,
    baseUrl: PropTypes.string.isRequired,
    baseHyUrl: PropTypes.string,
    apiToken: PropTypes.string.isRequired,
    bundleIdentifier: PropTypes.string.isRequired,
    fieldConfigurations: PropTypes.objectOf(
      PropTypes.oneOfType([PropTypes.object])
    ),
    hyJwtToken: PropTypes.string,
    authToken: PropTypes.string,
    authorizedMemberId: PropTypes.number,
    oauthRefreshToken: PropTypes.string,
    loggingEnabled: PropTypes.bool,
    googleApiKey: PropTypes.string,
    locale: PropTypes.string,
    piwikAuthToken: PropTypes.string,
    additionalData: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.object])),
    authMethod: PropTypes.string,
    inboxConfig: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.any])),
    getContainerSize: PropTypes.func
  }).isRequired,
  prescription: PropTypes.string,
  extendedConfiguration: PropTypes.shape({
    insuranceSettings: PropTypes.shape({
      profileId: PropTypes.string,
      insurance: PropTypes.shape({
        hyPayerId: PropTypes.string,
        payerId: PropTypes.string,
        payerName: PropTypes.string,
        inNetwork: PropTypes.bool,
        memberId: PropTypes.string,
        groupId: PropTypes.string,
        planId: PropTypes.string
      })
    }),
    priceProcedures: PropTypes.shape({
      zipCode: PropTypes.string,
      procedureName: PropTypes.string
    }),
    findFacilities: PropTypes.shape({
      zipCode: PropTypes.string,
      facilityType: PropTypes.string,
      favoriteList: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])),
      addToFavorite: PropTypes.func
    })
  }),
  isPageReloaded: PropTypes.bool
};

export default TdFlowWrapper;
